import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { Pagination as AntPagination, Button } from "antd";
import { Orientation } from "@/utils/enum";
// @ts-ignore
import styles from "./Pagination.styles.less";
const Pagination = ({ page, pageSize, totalItems, showLessItems, showQuickJumper, showSizeChanger, onPageChange, onPageSizeChange, }) => {
    const wrapperRef = useRef(null);
    const [orientation, setOrientation] = useState(Orientation.Horizontal);
    const lastPage = useMemo(() => Math.ceil(totalItems / pageSize), [totalItems, pageSize]);
    const { cantGoPreviousPage, cantGoNextPage } = useMemo(() => ({
        cantGoPreviousPage: page <= 1,
        cantGoNextPage: page >= lastPage,
    }), [page, lastPage]);
    const wrapperStyles = useMemo(() => {
        const newStyles = {
            display: "flex",
            alignItems: "center",
        };
        if (orientation === Orientation.Horizontal) {
            newStyles.flexDirection = "row";
            newStyles.justifyContent = "space-between";
        }
        else {
            newStyles.flexDirection = "column";
            newStyles.alignItems = "center";
        }
        return newStyles;
    }, [orientation]);
    const onPageChangeLocal = useCallback(newPage => {
        if (typeof onPageChange === "function") {
            onPageChange(Math.min(Math.max(1, newPage), lastPage));
        }
    }, [onPageChange, lastPage]);
    const onPageSizeChangeLocal = useCallback(newPageSize => {
        if (typeof onPageSizeChange === "function") {
            onPageSizeChange(newPageSize);
        }
    }, [onPageSizeChange]);
    const handleOnChange = useCallback((newPage, newPageSize) => {
        if (newPage !== page) {
            onPageChangeLocal(newPage);
        }
        if (newPageSize !== pageSize) {
            onPageSizeChangeLocal(newPageSize);
        }
    }, [page, pageSize, onPageChangeLocal, onPageSizeChangeLocal]);
    const handleGoToNextPage = useCallback(() => {
        onPageChangeLocal(page + 1);
    }, [page, onPageChangeLocal]);
    const handleGoToPreviousPage = useCallback(() => {
        onPageChangeLocal(page - 1);
    }, [page, onPageChangeLocal]);
    const previousButton = useMemo(() => (React.createElement(Button, { style: {
            borderRadius: "4px 0px 0px 4px",
            width: "100px",
            marginRight: orientation === Orientation.Vertical ? "4px" : 0,
        }, disabled: cantGoPreviousPage, onClick: handleGoToPreviousPage },
        React.createElement(ArrowLeftOutlined, null),
        "Previous")), [orientation, cantGoPreviousPage, handleGoToPreviousPage]);
    useEffect(() => {
        if (!wrapperRef.current) {
            return;
        }
        const resizeObserver = new ResizeObserver(([{ contentBoxSize }]) => {
            const { inlineSize: width } = contentBoxSize[0];
            if (width <= 485) {
                setOrientation(Orientation.Vertical);
            }
            else {
                setOrientation(Orientation.Horizontal);
            }
        });
        resizeObserver.observe(wrapperRef.current);
        // eslint-disable-next-line consistent-return
        return () => {
            resizeObserver.disconnect();
        };
    }, []);
    return (React.createElement("div", { ref: wrapperRef, style: wrapperStyles },
        orientation === Orientation.Horizontal && previousButton,
        totalItems > 0 && (React.createElement(AntPagination, { showLessItems: showLessItems, showQuickJumper: showQuickJumper, showSizeChanger: showSizeChanger, className: styles.strategyTable, total: totalItems, current: page, pageSize: pageSize, onChange: handleOnChange })),
        React.createElement("div", { style: {
                display: "flex",
                flexDirection: "row",
                marginTop: orientation === Orientation.Vertical ? "8px" : 0,
            } },
            orientation === Orientation.Vertical && previousButton,
            React.createElement(Button, { style: {
                    borderRadius: "0px 4px 4px 0px",
                    width: "100px",
                    marginLeft: orientation === Orientation.Vertical ? "4px" : 0,
                }, disabled: cantGoNextPage, onClick: handleGoToNextPage },
                "Next",
                React.createElement(ArrowRightOutlined, null)))));
};
Pagination.defaultProps = {
    showLessItems: false,
    showQuickJumper: false,
    showSizeChanger: false,
    onPageChange: () => { },
    onPageSizeChange: () => { },
};
export default Pagination;
